/** High-score tables (docs/spec/07 §2): ten lines each, initials entered arcade style. Pure. */

export type TableId = 'classic' | 'deep' | 'expedition';
export const TABLE_IDS: readonly TableId[] = ['classic', 'deep', 'expedition'];
export const TABLE_SIZE = 10;

export interface ScoreEntry {
  /** Three letters. */
  readonly initials: string;
  readonly score: number;
  /** How far they got: `M/3`, `DEPTH 12`. */
  readonly reached: string;
  /** YYYY-MM-DD. */
  readonly date: string;
  /** Played at Relaxed speed (docs/spec/07 §5). */
  readonly relaxed: boolean;
  /** Set on scores made here and sent to the world tables, so the same score is never listed twice. */
  readonly id?: string;
}

/** What a score id looks like: random, lower-case letters and digits. */
export const SCORE_ID = /^[a-z0-9]{8,32}$/;

export type Tables = Readonly<Record<TableId, readonly ScoreEntry[]>>;

const DEFAULT_NAMES = ['DIG', 'ORE', 'GEM', 'ROX', 'SID', 'VIC', 'PET', 'MOS', 'CIA', 'KIT'];

/** The tables a new player sees: modest scores to beat. */
export function defaultTables(): Tables {
  const make = (base: number, reached: (i: number) => string): ScoreEntry[] =>
    DEFAULT_NAMES.map((initials, i) => ({ initials, score: base - i * Math.floor(base / 12), reached: reached(i), date: '1984-01-01', relaxed: false }));
  return {
    classic: make(3000, (i) => `${'EDCBA'[Math.min(4, Math.floor(i / 2))] ?? 'A'}/1`),
    deep: make(2400, (i) => `${'DCBAA'[Math.min(4, Math.floor(i / 2))] ?? 'A'}/1`),
    expedition: make(2000, (i) => `DEPTH ${Math.max(1, 6 - Math.floor(i / 2))}`),
  };
}

/** Where `score` would land (0-based), or -1 if it does not make the table. */
export function rankFor(table: readonly ScoreEntry[], score: number): number {
  if (score <= 0) return -1;
  const i = table.findIndex((e) => score > e.score);
  if (i >= 0) return i;
  return table.length < TABLE_SIZE ? table.length : -1;
}

export function insertScore(tables: Tables, id: TableId, entry: ScoreEntry): { tables: Tables; rank: number } {
  const table = tables[id];
  const rank = rankFor(table, entry.score);
  if (rank < 0) return { tables, rank };
  const next = [...table.slice(0, rank), { ...entry, initials: cleanInitials(entry.initials) }, ...table.slice(rank)].slice(0, TABLE_SIZE);
  return { tables: { ...tables, [id]: next }, rank };
}

export function cleanInitials(s: string): string {
  return (s.toUpperCase().replace(/[^A-Z0-9 .!-]/g, '') + '   ').slice(0, 3);
}

export function emptyTables(): Tables {
  return { classic: [], deep: [], expedition: [] };
}

/** The valid entries of an untrusted list, best first, at most a table's worth. */
export function parseEntries(list: readonly unknown[]): ScoreEntry[] {
  return list
    .filter((e): e is ScoreEntry => typeof e === 'object' && e !== null && typeof (e as ScoreEntry).score === 'number')
    .map((e) => {
      const entry: ScoreEntry = { initials: cleanInitials(String(e.initials)), score: Math.max(0, Math.floor(e.score)), reached: String(e.reached ?? '').slice(0, 12), date: String(e.date ?? '').slice(0, 10), relaxed: e.relaxed === true };
      return typeof e.id === 'string' && SCORE_ID.test(e.id) ? { ...entry, id: e.id } : entry;
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, TABLE_SIZE);
}

/** Parse tables, keeping whatever is valid; a table that is missing comes from `fallback`. */
function parseWith(json: string | null, fallback: Tables): Tables {
  if (!json) return fallback;
  try {
    const raw = JSON.parse(json) as Record<string, unknown>;
    const out: Record<TableId, ScoreEntry[]> = { classic: [], deep: [], expedition: [] };
    for (const id of TABLE_IDS) {
      const list = raw[id];
      out[id] = Array.isArray(list) ? parseEntries(list) : [...fallback[id]];
    }
    return out;
  } catch {
    return fallback;
  }
}

/** Parse stored tables, keeping whatever is valid and filling the rest with defaults. */
export function parseTables(json: string | null): Tables {
  return parseWith(json, defaultTables());
}

/** Parse the world tables (the server's, or their saved copy): no defaults. */
export function parseWorldTables(json: string | null): Tables {
  return parseWith(json, emptyTables());
}

/**
 * The tables a player sees: their own (with the defaults) and the world's, best first. A score that
 * is in both - sent from here and come back from the server - is listed once.
 */
export function mergeTables(own: Tables, world: Tables): Tables {
  const merge = (a: readonly ScoreEntry[], b: readonly ScoreEntry[]): ScoreEntry[] => {
    const ids = new Set(a.map((e) => e.id).filter((id) => id !== undefined));
    // A stable sort keeps a player's own entry above a world entry with the same score.
    return [...a, ...b.filter((e) => e.id === undefined || !ids.has(e.id))].sort((x, y) => y.score - x.score).slice(0, TABLE_SIZE);
  };
  return { classic: merge(own.classic, world.classic), deep: merge(own.deep, world.deep), expedition: merge(own.expedition, world.expedition) };
}
